/** Réponse de `/login` quand le second facteur est requis : aucun jeton encore. */
export interface TwoFactorPendingResponse {
    two_factor: 'challenge' | 'enroll';
    challenge_id: string;
    method?: 'totp' | 'email';
    /** Compte en méthode e-mail : adresse masquée (« m•••@hotel.fr »). */
    email_hint?: string;
    /** Faux si le fournisseur mail a échoué : proposer « Renvoyer ». */
    email_sent?: boolean;
    /** Enrôlement : le lien « recevoir les codes par e-mail » est proposé. */
    email_allowed?: boolean;
}

/** Réponse finale de connexion, identique quel que soit le chemin (avec ou sans 2FA). */
export interface LoginSessionResponse {
    token: string;
    user: any;
    onboarding?: {
        status?: string | null;
        is_primary_admin?: boolean;
        current_step?: string | null;
    };
    /** Enrôlement uniquement : affichés une seule fois. */
    recovery_codes?: string[];
    /** Présent si « faire confiance à cet appareil » était coché. */
    trusted_device_token?: string;
    /** Connexion par code de secours. */
    recovery_codes_remaining?: number;
}

export type LoginResponse = TwoFactorPendingResponse | LoginSessionResponse;

/** `/login/two-factor` : session, ou (super admin en e-mail) passage à l'application. */
export type VerifyResponse = LoginSessionResponse | TwoFactorPendingResponse;

export function isTwoFactorPending(data: unknown): data is TwoFactorPendingResponse {
    return typeof data === 'object' && data !== null && 'two_factor' in data && 'challenge_id' in data;
}

export interface TwoFactorSetupResponse {
    secret: string;
    otpauth_uri: string;
}

export interface TrustedDevice {
    id: number;
    browser: string | null;
    platform: string | null;
    ip_address: string | null;
    expires_at: string;
}

export interface TwoFactorStatus {
    enabled: boolean;
    exempt: boolean;
    /** 2FA obligatoire : elle ne peut pas être désactivée (seul le support la réinitialise). */
    required: boolean;
    confirmed_at: string | null;
    recovery_codes_remaining: number;
    trusted_devices: TrustedDevice[];
    recovery_codes?: string[];
    method: 'totp' | 'email' | null;
    email_allowed: boolean;
    compliant: boolean;
}

/** Délai de renvoi du code e-mail, identique côté serveur (`RateLimiter`). */
export const EMAIL_RESEND_COOLDOWN_SECONDS = 60;

/** Le serveur signale un challenge expiré ou épuisé : retour au mot de passe. */
export function isChallengeExpired(error: unknown): boolean {
    const data = (error as { response?: { data?: { challenge_expired?: boolean } } })?.response?.data;
    return data?.challenge_expired === true;
}
